import { useEffect, useRef, useState } from "react";
import { ConfirmModal } from "../components/ConfirmModal.jsx";
import { PuzzleBoard } from "../components/games/PuzzleBoard.jsx";
import { PuzzleImagePicker } from "../components/games/PuzzleImagePicker.jsx";
import {
  GoogleAccountButton,
  useSoloAuth,
} from "../contexts/SoloAuthContext.jsx";
import { SoundButton } from "../contexts/SoundContext.jsx";
import { PUZZLE_IMAGES } from "../data/puzzleImages.js";
import { clearPuzzleProgress } from "../domain/puzzle.js";
import {
  registrarVitoriaQuebraCabeca,
  salvarRecordeQuebraCabeca,
} from "../services/ranking.js";

export const PUZZLE_DIFFICULTIES = [
  { size: 3, label: "Fácil (3x3)" },
  { size: 4, label: "Médio (4x4)" },
  { size: 5, label: "Difícil (5x5)" },
  { size: 6, label: "Mestre (6x6)" },
  { size: 7, label: "Lendário (7x7)" },
  { size: 8, label: "Impossível (8x8)" },
  { size: 16, label: "Extremo (16x16)" },
];

const SESSION_KEY = "@PartyGames:quebraCabecaSoloSessao";
const readSession = () => {
  try {
    const value = JSON.parse(localStorage.getItem(SESSION_KEY));
    return ["jogando", "fim"].includes(value?.etapa) ? value : null;
  } catch {
    return null;
  }
};
const removeSession = () => {
  try {
    localStorage.removeItem(SESSION_KEY);
  } catch {}
};

export function PuzzleSoloPage({ aoVoltar }) {
  const { googleUser } = useSoloAuth();
  const restored = useRef(readSession()).current;
  const initialSeconds = restored
    ? restored.etapa === "fim"
      ? restored.tempoFinal || 0
      : Math.max(
          0,
          Math.floor((Date.now() - (restored.inicioEm || Date.now())) / 1000),
        )
    : 0;
  const [stage, setStage] = useState(restored?.etapa || "selecao");
  const [difficulty, setDifficulty] = useState(restored?.dificuldade || 3);
  const [imageUrl, setImageUrl] = useState(restored?.imagemAtual || null);
  const [sessionId, setSessionId] = useState(restored?.sessaoId || 0);
  const [imageMode, setImageMode] = useState("aleatoria");
  const [seconds, setSeconds] = useState(initialSeconds);
  const secondsRef = useRef(initialSeconds);
  const [rankingSaved, setRankingSaved] = useState(
    Boolean(restored?.salvouRanking),
  );
  const [exitOpen, setExitOpen] = useState(false);
  const activeGame = useRef(restored?.etapa === "jogando");
  const startedAt = useRef(restored?.inicioEm || Date.now());
  const progressKey = `solo_${difficulty}_${sessionId}`;

  useEffect(() => {
    document.body.classList.toggle("puzzle-active", stage === "jogando");
    return () => document.body.classList.remove("puzzle-active");
  }, [stage]);

  useEffect(() => {
    if (stage !== "jogando") return undefined;
    const interval = window.setInterval(() => {
      setSeconds((current) => {
        const next = current + 1;
        secondsRef.current = next;
        return next;
      });
    }, 1000);
    return () => window.clearInterval(interval);
  }, [stage]);

  useEffect(() => {
    try {
      if (stage === "jogando" || stage === "fim") {
        localStorage.setItem(
          SESSION_KEY,
          JSON.stringify({
            etapa: stage,
            dificuldade: difficulty,
            imagemAtual: imageUrl,
            sessaoId: sessionId,
            salvouRanking: rankingSaved,
            inicioEm: startedAt.current,
            tempoFinal: stage === "fim" ? secondsRef.current : null,
          }),
        );
      } else removeSession();
    } catch {}
  }, [stage, difficulty, imageUrl, sessionId, rankingSaved]);

  useEffect(() => {
    if (stage !== "fim" || !googleUser || rankingSaved) return;
    Promise.all([
      salvarRecordeQuebraCabeca(googleUser, difficulty, secondsRef.current),
      registrarVitoriaQuebraCabeca(googleUser, difficulty),
    ])
      .then(() => setRankingSaved(true))
      .catch((error) =>
        console.error("Erro ao salvar ranking do quebra-cabeça:", error),
      );
  }, [stage, googleUser, rankingSaved, difficulty]);

  const start = (nextDifficulty, selectedImage = null) => {
    const image =
      selectedImage ||
      PUZZLE_IMAGES[Math.floor(Math.random() * PUZZLE_IMAGES.length)];
    setDifficulty(nextDifficulty);
    setImageUrl(image.url);
    setSeconds(0);
    secondsRef.current = 0;
    setRankingSaved(false);
    startedAt.current = Date.now();
    setSessionId(Date.now());
    activeGame.current = true;
    setStage("jogando");
  };

  const finish = () => {
    if (!activeGame.current) return;
    activeGame.current = false;
    setStage("fim");
  };

  const leave = () => {
    clearPuzzleProgress(progressKey);
    removeSession();
    aoVoltar();
  };

  if (stage === "selecao") {
    return (
      <div className="container-sm text-center puzzle-solo-selection">
        <button className="puzzle-back" onClick={aoVoltar} type="button">
          ← Voltar
        </button>
        <h2 className="title">🕹️ Quebra-Cabeça Solo</h2>
        <p className="subtitle mb-40">
          Escolha a dificuldade e monte a imagem no menor tempo possível.
        </p>
        <GoogleAccountButton />
        <div
          className="puzzle-image-mode"
          role="group"
          aria-label="Modo de imagem"
        >
          <button
            className={imageMode === "aleatoria" ? "selected" : ""}
            onClick={() => setImageMode("aleatoria")}
            type="button"
          >
            🎲 Imagem Aleatória
          </button>
          <button
            className={imageMode === "escolher" ? "selected" : ""}
            onClick={() => setImageMode("escolher")}
            type="button"
          >
            🖼️ Escolher Imagem
          </button>
        </div>
        <div className="card puzzle-difficulty-list">
          {PUZZLE_DIFFICULTIES.map(({ size, label }) => (
            <button
              className="btn btn-primary"
              key={size}
              onClick={() => {
                if (imageMode === "escolher") {
                  setDifficulty(size);
                  setStage("escolha-imagem");
                } else start(size);
              }}
              type="button"
            >
              {label} {size === 16 && "⚠️"}
            </button>
          ))}
        </div>
        <p className="puzzle-extreme-warning">
          ⚠️ 16x16 tem 256 peças — desafio pra quem já mandou bem nas outras
          dificuldades.
        </p>
      </div>
    );
  }

  if (stage === "escolha-imagem") {
    return (
      <div className="container-sm text-center puzzle-picker-page">
        <button
          className="puzzle-back"
          onClick={() => setStage("selecao")}
          type="button"
        >
          ← Voltar
        </button>
        <h2 className="title">🖼️ Escolha a Imagem</h2>
        <p className="subtitle mb-40">
          Selecione a imagem que você vai montar na dificuldade {difficulty}x
          {difficulty}.
        </p>
        <div className="card puzzle-picker-card">
          <PuzzleImagePicker
            images={PUZZLE_IMAGES}
            onSelect={(image) => start(difficulty, image)}
          />
        </div>
      </div>
    );
  }

  if (stage === "fim") {
    return (
      <div className="container-sm text-center puzzle-solo-result">
        <div className="card puzzle-round-result won">
          <h3>🎉 Quebra-Cabeça Montado!</h3>
          <div className="puzzle-completed-image">
            <img src={imageUrl} alt="Quebra-Cabeça completo" />
          </div>
          <p className="puzzle-result-summary">
            Dificuldade{" "}
            <strong>
              {difficulty}x{difficulty}
            </strong>
            , concluído em <strong>{seconds}s</strong>
          </p>
          {googleUser ? (
            rankingSaved && (
              <p className="info-box">🏆 Resultado salvo no seu Ranking!</p>
            )
          ) : (
            <div className="puzzle-login-prompt">
              <p>Entre com Google para salvar esse resultado no ranking:</p>
              <GoogleAccountButton />
            </div>
          )}
          <div className="puzzle-result-actions">
            <button
              className="btn btn-primary"
              onClick={() => start(difficulty)}
              type="button"
            >
              🔁 Jogar de Novo
            </button>
            <button
              className="btn puzzle-secondary"
              onClick={() => setStage("selecao")}
              type="button"
            >
              🔀 Trocar Dificuldade
            </button>
            <button className="btn puzzle-exit" onClick={leave} type="button">
              Sair
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="puzzle-game-shell puzzle-solo-shell">
      <header className="puzzle-game-header">
        <button
          className="puzzle-header-exit"
          onClick={() => setExitOpen(true)}
          type="button"
        >
          🚪 Sair
        </button>
        <div>
          <SoundButton variante="inline" />
          <h2>
            🕹️ Solo {difficulty}x{difficulty}
          </h2>
        </div>
        <strong>⏱️ {seconds}s</strong>
      </header>
      <div className="puzzle-board-area">
        <PuzzleBoard
          imageUrl={imageUrl}
          rows={difficulty}
          columns={difficulty}
          onComplete={finish}
          persistKey={progressKey}
        />
      </div>
      <ConfirmModal
        isOpen={exitOpen}
        title="Sair da partida?"
        message="Seu progresso nesse quebra-cabeça será perdido e não contará para o ranking."
        confirmText="Sim, sair"
        onConfirm={() => {
          setExitOpen(false);
          leave();
        }}
        onCancel={() => setExitOpen(false)}
      />
    </div>
  );
}
